import { useState } from "react";
import { Alert, Badge, Card, DataTable, Field, Input, PageHeader, Select, Spinner } from "@empite/ui-components";
import type { Operator, Reason, TenantAPIKey } from "../admin-client";
import { useCreateTenantAPIKey, useRevokeTenantAPIKey, useTenantAPIKeys } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, isPlatformStaff } from "./guards";
import { NotPermitted } from "./packages";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";

/**
 * The tenant's API keys.
 *
 * A tenant API key is what the organisation's OWN backend presents in
 * `X-Tenant-Api-Key` to `/v1/tenant`: it vouches for the organisation's users,
 * mints them exchanged access tokens for the inference gateway, and reads their
 * wallets. It is the seam that lets a company keep its own sign-in and still
 * bill through this platform — see docs/operations/identity-and-devices.md,
 * "Tenant API keys and the token exchange".
 *
 * The permission is tenant_api_keys:manage, held by tenant_owner and
 * super_admin and by NOBODY else. It is deliberately not team:manage: a key is
 * a standing licence to bill any of the tenant's customers, and the person
 * trusted to add a colleague is not automatically trusted to hand that licence
 * to a server.
 *
 * The plaintext is shown ONCE, in the response that minted it. The list holds
 * a prefix and a digest; lose the key and the answer is revoke and mint.
 */
const MANAGE = "tenant_api_keys:manage";

const EXPIRY_OPTIONS: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 0, label: "Never — a production integration" },
  { seconds: 30 * 24 * 3600, label: "30 days" },
  { seconds: 90 * 24 * 3600, label: "90 days" },
  { seconds: 365 * 24 * 3600, label: "1 year" },
];

export function APIKeys() {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, MANAGE) ? (
          <APIKeysScreen operator={session.operator} />
        ) : (
          <NotPermitted title="API keys" permission={MANAGE} />
        )
      }
    </RequireOperator>
  );
}

function APIKeysScreen({ operator }: { operator: Operator }) {
  // Platform staff belong to no tenant and must name the one they are acting
  // in; the server refuses a tenant operator that sends one.
  const [tenantID, setTenantID] = useState("");
  const staff = isPlatformStaff(operator);
  const scoped = staff ? tenantID.trim() : undefined;
  const keys = useTenantAPIKeys(scoped || undefined);

  return (
    <>
      <PageHeader
        title="API keys"
        description="The credential this company's own backend presents to the billing platform. It vouches for your users and mints them tokens; treat it like a database password."
      />
      {staff ? (
        <Card title="Which tenant">
          <Alert tone="warning" title="You belong to no tenant">
            These are a TENANT's keys. Signed in as platform staff, name the tenant you are
            acting in — its id from the roster — and every act on this page lands on it.
          </Alert>
          <Field label="Tenant id" htmlFor="api-keys-tenant">
            <Input
              id="api-keys-tenant"
              value={tenantID}
              onChange={(event) => setTenantID(event.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </Field>
        </Card>
      ) : null}
      {!staff || scoped ? (
        <>
          <MintForm tenantID={scoped} />
          <Card title="Keys">
            {keys.isPending ? (
              <Spinner label="Loading the keys" />
            ) : keys.error ? (
              <ErrorAlert error={keys.error} title="Could not load the keys" />
            ) : (
              <KeyTable rows={keys.data?.api_keys ?? []} tenantID={scoped} />
            )}
          </Card>
        </>
      ) : null}
    </>
  );
}

function MintForm({ tenantID }: { tenantID?: string }) {
  const mint = useCreateTenantAPIKey();
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<number>(0);
  // The plaintext, shown ONCE. Kept in component state only; a reload loses
  // it, which is the point — nothing on this side can produce it again.
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);

  const label = name.trim();
  const ready = label !== "";

  async function run(reason: Reason) {
    const result = await mint.mutateAsync({
      body: {
        name: label,
        ...(expiry > 0 ? { expires_in_seconds: expiry } : {}),
        ...(tenantID ? { tenant_id: tenantID } : {}),
      },
      reason,
    });
    setIssued({ name: result.api_key.name, key: result.key });
    setName("");
  }

  return (
    <Card title="Mint a key">
      <ErrorAlert error={mint.error} title="That key was not minted" />
      {issued ? (
        <Alert tone="success" title={`Key "${issued.name}" minted`}>
          <p>
            Copy it now. It is shown here once and never again — the platform keeps only a digest.
            Put it in your backend's configuration as the <code>X-Tenant-Api-Key</code> header
            value for <code>/v1/tenant</code>.
          </p>
          <p>
            <code>{issued.key}</code>
          </p>
        </Alert>
      ) : null}
      <Field
        label="Name"
        htmlFor="api-key-name"
        hint="Where it will be used — “production backend”, “staging”, “CI”. It is how you will tell keys apart when one has to go."
      >
        <Input id="api-key-name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="Expires" htmlFor="api-key-expiry">
        <Select
          id="api-key-expiry"
          value={String(expiry)}
          onChange={(event) => setExpiry(Number(event.target.value))}
        >
          {EXPIRY_OPTIONS.map((entry) => (
            <option key={entry.seconds} value={entry.seconds}>
              {entry.label}
            </option>
          ))}
        </Select>
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={mint.isPending}
          disabled={!ready}
          mutation={{
            title: `Mint the key “${label || "unnamed"}”`,
            tone: "primary",
            description: (
              <>
                Creates a credential that can vouch for ANY of this company's users and mint them
                tokens that spend from their wallets. The plaintext is shown once.
              </>
            ),
            confirmLabel: "Mint the key",
            run,
          }}
        >
          Mint key
        </ReasonButton>
      </div>
    </Card>
  );
}

function KeyTable({ rows, tenantID }: { rows: TenantAPIKey[]; tenantID?: string }) {
  const revoke = useRevokeTenantAPIKey();

  return (
    <>
      <ErrorAlert error={revoke.error} title="That key was not revoked" />
      <DataTable<TenantAPIKey>
        rows={rows}
        rowKey={(row) => row.id}
        empty="No key has been minted yet. Your backend cannot reach /v1/tenant until one is."
        columns={[
          {
            key: "name",
            header: "Key",
            render: (row) => (
              <>
                <strong>{row.name}</strong>
                <div className="esm-muted-text">
                  <code>{row.key_prefix}…</code>
                </div>
              </>
            ),
          },
          {
            key: "status",
            header: "Status",
            // `live` and not a comparison against expires_at: the server
            // resolved the expiry against its own clock.
            render: (row) =>
              row.status === "active" && !row.live ? (
                <Badge tone="warning">expired</Badge>
              ) : (
                <StatusBadge status={row.status} />
              ),
          },
          { key: "created", header: "Minted", render: (row) => when(row.created_at) },
          {
            key: "expires",
            header: "Expires",
            render: (row) =>
              row.expires_at ? when(row.expires_at) : <span className="esm-muted-text">never</span>,
          },
          {
            key: "used",
            header: "Last used",
            render: (row) =>
              row.last_used_at ? when(row.last_used_at) : <span className="esm-muted-text">never</span>,
          },
          {
            key: "act",
            header: "",
            render: (row) =>
              row.status === "active" ? (
                <ReasonButton
                  busy={revoke.isPending}
                  mutation={{
                    title: `Revoke “${row.name}”`,
                    description: (
                      <>
                        Every request carrying this key is refused from the next one on. Tokens it
                        already minted live out their own hour. The row stays in this list as
                        revoked.
                      </>
                    ),
                    confirmLabel: "Revoke the key",
                    run: async (reason) => {
                      await revoke.mutateAsync({ keyID: row.id, reason, tenantID });
                    },
                  }}
                >
                  Revoke
                </ReasonButton>
              ) : (
                <span className="esm-muted-text">{row.revoked_at ? when(row.revoked_at) : ""}</span>
              ),
          },
        ]}
      />
    </>
  );
}
